import {
    zodFirstNameValidation,
    zodLastNameValidation,
} from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { router } from 'expo-router';
import { useCallback, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    Form,
    ScreenContainer,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useCreatePartnerMember } from '../../auth/hooks/start-membership/useCreatePartnerMember';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { captureException } from '../../logger/Logger';
import {
    SUCCESS_TOAST_DURATION,
    SuccessToastIcon,
} from '../../member-profile/components/SuccessToast';
import { useUser } from '../../user/providers/UserProvider';
import { SUPPORT_EMAIL } from '../types/constants';

const addPartnerSchema = z.object({
    partnerFirstName: zodFirstNameValidation,
    partnerLastName: zodLastNameValidation,
    partnerEmail: z
        .string()
        .email("Please provide your partner's email address."),
});

type AddPartnerSchema = z.infer<typeof addPartnerSchema>;

const AddPartner = () => {
    const toast = useToastController();
    const user = useAuthedUser();
    const { refetch } = useUser();
    const { createPartnerMember, isCreatingPartnerMember } =
        useCreatePartnerMember();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        handleSubmit,
        formState: { isValid, errors },
        register,
        control,
        setFocus,
        setError,
    } = useForm<AddPartnerSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(addPartnerSchema),
        defaultValues: {
            partnerFirstName: '',
            partnerLastName: '',
            partnerEmail: '',
        },
    });

    const onSubmit: SubmitHandler<AddPartnerSchema> = useCallback(
        (data) => {
            setIsSubmitting(true);
            createPartnerMember({
                userId: user.id,
                partnerEmail: data.partnerEmail,
                partnerFirstName: data.partnerFirstName,
                partnerLastName: data.partnerLastName,
                onSuccess: async () => {
                    await refetch?.();
                    setIsSubmitting(false);
                    toast.show('Partner added!', {
                        duration: SUCCESS_TOAST_DURATION,
                        icon: SuccessToastIcon,
                    });
                    router.back();
                },
                onError: (error) => {
                    setIsSubmitting(false);
                    captureException(error, {
                        data: 'Error adding partner from settings',
                    });
                    if (error.message === 'Conflict Exception') {
                        setError('partnerEmail', {
                            type: 'manual',
                            message: `Your partner's email address is already in our system. Please reach out to ${SUPPORT_EMAIL} if you need assistance.`,
                        });
                    } else {
                        toast.show('Something went wrong!', {
                            duration: 2000,
                        });
                    }
                },
            });
        },
        [createPartnerMember, refetch, setError, toast, user.id],
    );

    const isFormSubmitting = isSubmitting || isCreatingPartnerMember;

    return (
        <ScreenContainer title="Add your partner" hasBackButton={true}>
            <YStack gap={24} justifyContent="flex-start">
                <YStack gap={8}>
                    <BodyLargeRegular>Add your partner</BodyLargeRegular>
                    <BodyMediumRegular color="$gray1000">
                        We&apos;ll send your partner an email to get their
                        account set up.
                    </BodyMediumRegular>
                </YStack>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    <YStack gap={16}>
                        <XStack gap={12}>
                            <YStack flex={1}>
                                <ControlledInput
                                    disabled={isFormSubmitting}
                                    name="partnerFirstName"
                                    label="First name"
                                    placeholder="First name"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    autoComplete="name-given"
                                    textContentType="name"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('partnerLastName');
                                    }}
                                />
                            </YStack>
                            <YStack flex={1}>
                                <ControlledInput
                                    disabled={isFormSubmitting}
                                    name="partnerLastName"
                                    label="Last name"
                                    placeholder="Last name"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    autoComplete="name-family"
                                    textContentType="familyName"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('partnerEmail');
                                    }}
                                />
                            </YStack>
                        </XStack>

                        <ControlledInput
                            disabled={isFormSubmitting}
                            name="partnerEmail"
                            label="Email address"
                            placeholder="Enter email address"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="none"
                            inputMode="email"
                            keyboardType="email-address"
                            // @ts-expect-error We don't want autoComplete set to 'email' to avoid autofill of the wrong email so using something the browser is unfamiliar with should prevent autofill
                            autoComplete="partner-email"
                        />
                    </YStack>
                </Form>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Add partner"
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid || isFormSubmitting}
                    marginTop="auto"
                />
            </YStack>
        </ScreenContainer>
    );
};

export default AddPartner;
